/**
 * Builds the canonical location page path in the legacy format (without locale):
 * /{category}/{slug}/LE-{id}/{tab}
 * e.g. /Eventlocation-Berlin/Academie_Lounge/LE-35177/0
 * Use withLocale(getLocationPath(...)) for full URLs.
 */

const UMLAUT_MAP: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  Ä: "Ae",
  Ö: "Oe",
  Ü: "Ue",
  ß: "ss",
};

function normalizeUmlauts(s: string): string {
  return s.replace(/[äöüÄÖÜß]/g, (c) => UMLAUT_MAP[c] ?? c);
}

/** For category segment: replace spaces with dash, normalize umlauts, remove invalid chars */
function slugifyCategoryPart(s: string): string {
  const normalized = normalizeUmlauts(s.trim());
  return normalized
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "location";
}

/** For name slug: replace spaces with underscore, normalize umlauts */
function slugifyName(s: string): string {
  const normalized = normalizeUmlauts(s.trim());
  return normalized
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "") || "location";
}

export type LocationPathInput = {
  id: number;
  name?: string | null;
  nameForm?: { german?: string; english?: string } | null;
  city?: string | null;
  locationTypes?: Array<{ id: number; name: string }> | null;
};

/**
 * Returns the canonical path for a location page.
 * Category = primary location type + city (e.g. Eventlocation-Berlin).
 * Slug = location name (e.g. Academie_Lounge).
 */
export function getLocationPath(
  location: LocationPathInput,
  tab: number = 0
): string {
  const primaryType =
    location.locationTypes?.[0]?.name ??
    location.locationTypes?.sort((a, b) => a.name.localeCompare(b.name))[0]
      ?.name ??
    "Eventlocation";
  const city = location.city?.trim() || "Unknown";
  const category = `${slugifyCategoryPart(primaryType)}-${slugifyCategoryPart(city)}`;

  const name =
    location.nameForm?.german?.trim() ||
    location.name?.trim() ||
    "Location";
  const slug = slugifyName(name);

  return `/${category}/${slug}/LE-${location.id}/${tab}`;
}

/** Section ids in order (SectionNav) for mapping tab index to hash */
export const LOCATION_SECTION_IDS = [
  "overview",
  "rooms",
  "hotel",
  "details",
  "downloads",
] as const;

export function getLocationSectionHash(tab: number): string {
  const id = LOCATION_SECTION_IDS[tab];
  return id ? `#${id}` : "";
}
